import React, { useState } from "react";
import { Container, Typography, Box, TextField, Button, Card, Grid, Alert, Snackbar } from "@mui/material";
import { useTranslation } from "react-i18next";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { send } from "emailjs-com";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";

const EMAILJS_SERVICE_ID = "service_ew404tr";
const EMAILJS_TEMPLATE_ID = "template_nheufxc";
const EMAILJS_USER_ID = "t21C4D7sSuBLRlHLh";

const Contact = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, success: true, msg: "" });
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const newErrors = {};
    if (!form.name) newErrors.name = t("required");
    if (!form.email) newErrors.email = t("required");
    else if (!/\S+@\S+\.\S+/.test(form.email)) newErrors.email = t("invalid_email");
    if (!form.message) newErrors.message = t("required");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

    const handleChange = e =>
    setForm(f => ({
      ...f,
      [e.target.name]: e.target.value
    }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        form, // { name, email, message }
        EMAILJS_USER_ID
      );
      setSnackbar({ open: true, success: true, msg: t("success_message") });
      setForm({ name: "", email: "", message: "" });
      setErrors({});
    } catch {
      setSnackbar({ open: true, success: false, msg: t("error_message") });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t("meta_title_contact")}</title>
        <meta name="description" content={t("meta_description")} />
        <link rel="alternate" hrefLang="en" href="brainstorm-visions.com/en" />
        <link rel="alternate" hrefLang="el" href="brainstorm-visions.com/en/el" />
      </Helmet>
      <Container sx={{ py: 8 }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Card
              component={motion.div}
              initial={{ opacity: 0, x: -60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              sx={{ p: 4, boxShadow: 4, background: "linear-gradient(120deg, #57a1d8 40%, #1e293b 100%)", color: "#fff" }}
            >
              <Typography variant="h4" fontWeight="bold" mb={2}>{t("contact_form_title")}</Typography>
              <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  name="name"
                  label={t("name")}
                  value={form.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  variant="filled"
                  InputProps={{ style: { background: "#fff" } }}
                />
                <TextField
                  name="email"
                  label={t("email")}
                  value={form.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  variant="filled"
                  InputProps={{ style: { background: "#fff" } }}
                />
                <TextField
                  name="message"
                  label={t("message")}
                  value={form.message}
                  onChange={handleChange}
                  error={!!errors.message}
                  helperText={errors.message}
                  variant="filled"
                  multiline
                  rows={4}
                  InputProps={{ style: { background: "#fff" } }}
                />
                <Button type="submit" variant="contained" color="secondary" disabled={loading} sx={{ fontWeight: "bold" }}>
                  {t("send")}
                </Button>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component={motion.div}
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              sx={{ p: 4 }}
            >
              <Typography variant="h5" mb={2}><EmailIcon sx={{ mr: 1, verticalAlign: "middle" }} /> info@brainstorm-visions.com</Typography>
              <Typography variant="h6" mb={2}><LocationOnIcon sx={{ mr: 1, verticalAlign: "middle" }} /> 1908 Thomes Ave STE 12392, Cheyenne, WY 82001, USA</Typography>
              <Typography variant="body1">EIN: 30-1448596</Typography>
            </Box>
          </Grid>
        </Grid>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4500}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity={snackbar.success ? "success" : "error"} sx={{ width: "100%" }}>
            {snackbar.msg}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default Contact;
